import React, { Fragment, useState, useEffect } from 'react'
import './DocumentHeader.scss'
import {
  FolderAddOutlined,
  FileAddOutlined,
  RightOutlined, FileTwoTone
} from '@ant-design/icons'
import { Button, Dropdown, Input, message, Modal } from 'antd'
import {
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaDesktop,
  FaDownload,
  FaExclamationTriangle
} from 'react-icons/fa'
import axios from 'axios';
import DocEditor from '../DocEditor'
import { fetchRefreshToken } from '../../../../../services/api/user'
import SOPModal from '../SOPModal'
import { config, general } from '../../../../../constants'
import { useTranslation } from 'react-i18next'
import { getUserPermissions } from '../utils/getPermission'

const { Search } = Input

const DocumentHeader = ({
  orgUserList,
  docEditorFile,
  userId,
  userName,
  userOrg,
  tab,
  defaultQuery,
  isSuperAdmin,
  isAdmin,
  query,
  handleChangeSearch,
  openUploadModal,
  setIsCreateFolderModalVisible,
  user,
  shlToken,
  setIsSOPModalVisible,
  documentData,
  handleDocTypeModalVisible,
  isSOPModalVisible,
  resetDocumentData,
  upload
}) => {
  console.log("docEditorFile log", docEditorFile)
  const [visible, setVisible] = useState(false)
  const [refreshToken, setRefreshToken] = useState(null);
  const [isCreatingFile, setIsCreatingFile] = useState(false)
  const [creatingFileType, setCreatingFileType] = useState('')
  const [showDocEditor, setShowDocEditor] = useState(false)
  const [editorState, setEditorState] = useState({
    editorParams: {
      documentName: '',
      documentId: '',
      userId: '',
      userName: ''
    },
    isShowEditor: false
  })
  const [docPath, setDocPath] = useState('')
  const [docName, setDocName] = useState('')
  const [selectedDocType, setSelectedDocType] = useState(null)
  const [sopData, setSopData] = useState(null); // store API response data

  useEffect(() => {
    if (sopData && sopData.data) {
      console.log("sopData updated, opening modal:", sopData);
      setIsSOPModalVisible(true);
      resetDocumentData();
    }
  }, [sopData]);

  useEffect(() => {
    console.log("editorState updated:", editorState)
  }, [editorState])

  useEffect(() => {
    const fetchTokens = async () => {
      const refresh = await fetchRefreshToken();
      setRefreshToken(refresh);
    };

    fetchTokens();
  }, []);

  useEffect(() => {
    const createDocument = async () => {
      // 1️⃣ Only run if user clicked "Create"
      if (!documentData || !documentData.isCreate) return;

      // 2️⃣ Validate type and name
      if (!documentData.type) {
        console.warn("⛔ Document type missing");
        return;
      }
      if (!documentData.name || !documentData.name.trim()) {
        console.warn("⛔ Document name missing");
        return;
      }

      // 3️⃣ Check upload function
      if (!upload) {
        console.error('Upload function not provided')
        return
      }

      // 4️⃣ Close the modal since user has selected a document type
      handleDocTypeModalVisible(false)

      let fileConfig = null

      // 5️⃣ Determine file configuration based on document type
      switch (selectedDocType) {
        case 'writer':
          setCreatingFileType('Writer Document')

          // Determine which template to create based on document type
          let sopContent = '';

          if (documentData.type === 'global_sop_keystroke') {
            // Create formatted Global SOP Key Stroke Template
            sopContent = `{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}{\\f1\\fnil\\fcharset0 Arial;}{\\f2\\fnil\\fcharset2 Symbol;}}
{\\colortbl ;\\red0\\green0\\blue255;\\red0\\green0\\blue0;\\red192\\green192\\blue192;\\red47\\green84\\blue150;}
{\\*\\generator Riched20 10.0.19041}\\viewkind4\\uc1 
\\pard\\sa200\\sl276\\slmult1\\qc\\b\\f0\\fs32 GLOBAL STANDARD OPERATING PROCEDURE\\par
\\fs28 KEY STROKE DOCUMENTATION\\par
\\pard\\sa200\\sl276\\slmult1\\b0\\fs22\\par

{\\pard\\box\\brdrs\\brdrw10\\brsp20\\shading10000\\cbpat3\\sa100\\sb100\\sl240\\slmult1\\qc\\b\\fs24\\ul Document Control Information\\ulnone\\b0\\fs22\\par}
\\pard\\sa100\\sl240\\slmult1\\par

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Document Title\\b0\\cell Global SOP - Key Stroke for ${documentData.name || '[Process Name]'}\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Document Number\\b0\\cell DHL-SOP-KS-${Date.now().toString().slice(-3)}\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Version\\b0\\cell 1.0\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Effective Date\\b0\\cell ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Classification\\b0\\cell Internal Use Only\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 1. PURPOSE\\par}
\\pard\\sa100\\sl240\\slmult1\\cf2\\b0\\fs22 This Standard Operating Procedure (SOP) provides detailed key stroke documentation for ${documentData.name || '[Process Name]'}. It ensures consistent, accurate, and efficient execution of system operations across all teams and locations.\\par
\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 2. SCOPE\\par}
\\pard\\sa100\\sl240\\slmult1\\b\\fs24 2.1 Applicability\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1\\b0\\fs22\\f2\\'b7\\tab\\f0 Geographical Coverage: Global\\par
\\f2\\'b7\\tab\\f0 Business Units: All DHL Operations\\par
\\f2\\'b7\\tab\\f0 Systems: [Specify system names]\\par
\\pard\\sa100\\sl240\\slmult1\\par
\\b\\fs24 2.2 Exclusions\\par
\\b0\\fs22 [List any exclusions or exceptions to this SOP]\\par
\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 3. DEFINITIONS AND ABBREVIATIONS\\par}
\\pard\\sa100\\sl240\\slmult1\\b0\\fs22\\par

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Term\\b0\\cell\\b Definition\\b0\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100 Key Stroke\\cell Detailed step-by-step system navigation instructions\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100 SOP\\cell Standard Operating Procedure\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100 QA\\cell Quality Assurance\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 4. ROLES AND RESPONSIBILITIES\\par}
\\pard\\sa100\\sl240\\slmult1\\b0\\fs22\\par

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Role\\b0\\cell\\b Responsibilities\\b0\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Operator/User\\b0\\cell Execute key stroke procedures accurately and efficiently\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Team Leader\\b0\\cell Monitor compliance and provide guidance to team members\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Process Owner\\b0\\cell Maintain and update SOP documentation\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx3500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Quality Assurance\\b0\\cell Audit adherence to procedures and standards\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 5. PREREQUISITES\\par}
\\pard\\sa100\\sl240\\slmult1\\b\\fs24 5.1 System Access\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1\\b0\\fs22\\f2\\'b7\\tab\\f0 Required system login credentials\\par
\\f2\\'b7\\tab\\f0 Appropriate user permissions level\\par
\\f2\\'b7\\tab\\f0 VPN access (if applicable)\\par
\\pard\\sa100\\sl240\\slmult1\\par
\\b\\fs24 5.2 Required Information\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1\\b0\\fs22\\f2\\'b7\\tab\\f0 Customer information\\par
\\f2\\'b7\\tab\\f0 Shipment details\\par
\\f2\\'b7\\tab\\f0 Reference numbers\\par
\\pard\\sa100\\sl240\\slmult1\\par
\\b\\fs24 5.3 Training Requirements\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1\\b0\\fs22\\f2\\'b7\\tab\\f0 Completion of mandatory training modules\\par
\\f2\\'b7\\tab\\f0 System familiarization\\par
\\f2\\'b7\\tab\\f0 Security clearance level\\par
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 6. KEY STROKE PROCEDURE\\par}
\\pard\\sa100\\sl240\\slmult1\\b\\fs24 6.1 Process Step 1 - [Title]\\par
\\b0\\fs22\\par
\\b Objective:\\b0  [What this step accomplishes]\\par
\\par
\\b Key Strokes:\\b0\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1 1.\\tab\\b Action:\\b0  Navigate to [Screen/Module Name]\\par
\\pard\\fi-360\\li1440\\sa50\\sl240\\slmult1\\f2\\'b7\\tab\\f0\\b Key Stroke:\\b0  Click on [Menu] \\u8594? [Submenu] \\u8594? [Option]\\par
\\f2\\'b7\\tab\\f0\\b Expected Result:\\b0  System displays [description]\\par
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 12. RELATED DOCUMENTS AND REFERENCES\\par}
\\pard\\sa100\\sl240\\slmult1\\b0\\fs22\\par
\\pard\\fi-360\\li720\\sa100\\sl240\\slmult1\\f2\\'b7\\tab\\f0 {\\field{\\*\\fldinst HYPERLINK "https://www.youtube.com/watch?v=hKw6cRKcqzY"}{\\fldrslt\\ul\\cf1 Training Video - Key Stroke Process Guide}}\\par
\\f2\\'b7\\tab\\f0 System User Guide - [Document Number]\\par
\\f2\\'b7\\tab\\f0 Quick Reference Guide - [Document Number]\\par
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\keep\\keepn\\sb240\\sa60\\cf4\\b\\fs28 15. APPROVAL SIGNATURES\\par}
\\pard\\sa100\\sl240\\slmult1\\b0\\fs22\\par

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx2500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx5000
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx7500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Role\\b0\\cell\\b Name\\b0\\cell\\b Signature\\b0\\cell\\b Date\\b0\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx2500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx5000
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx7500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Prepared By\\b0\\cell [Name]\\cell\\cell [Date]\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx2500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx5000
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx7500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Reviewed By\\b0\\cell [Name]\\cell\\cell [Date]\\cell\\row

\\trowd\\trgaph70\\trleft-70\\trbrdrl\\brdrs\\brdrw10\\trbrdrt\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx2500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx5000
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx7500
\\clbrdrl\\brdrw10\\brdrs\\clbrdrt\\brdrw10\\brdrs\\clbrdrr\\brdrw10\\brdrs\\clbrdrb\\brdrw10\\brdrs\\cellx9500
\\pard\\intbl\\sa100\\b Approved By\\b0\\cell [Name]\\cell\\cell [Date]\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par

{\\pard\\brdrb\\brdrs\\brdrw10\\brsp20\\sa100\\qc\\i\\fs18 Document Number: DHL-SOP-KS-${Date.now().toString().slice(-3)} | Version: 1.0 | Confidential - Internal Use Only\\par}
\\pard\\sa200\\sl276\\slmult1\\i0\\par
}`;
          } else if (documentData.type === 'bp_specific_sop_keystroke') {
            // Create formatted DHL BP-Specific SOP Key Stroke Template
            sopContent = `{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Arial;}{\\f1\\fnil\\fcharset0 Calibri;}}
{\\colortbl ;\\red212\\green5\\blue17;\\red255\\green204\\blue0;\\red0\\green0\\blue0;\\red221\\green221\\blue221;}
{\\*\\generator Riched20 10.0.19041}\\viewkind4\\uc1 
\\pard\\sa200\\sl276\\slmult1\\qc\\cf1\\b\\f0\\fs48 DHL Express\\par
\\fs36 BP-SPECIFIC STANDARD OPERATING PROCEDURE\\par
\\fs32 Key Stroke Template\\par
\\pard\\sa200\\sl276\\slmult1\\cf0\\b0\\fs22\\par
\\pard\\sa0\\sl276\\slmult0\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx4500\\cellx9000\\pard\\intbl\\cf1\\b Document Information\\cell Details\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx4500\\cellx9000\\pard\\intbl\\cf0\\b0 Document Title:\\cell BP-Specific SOP - Key Stroke Template\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx4500\\cellx9000\\pard\\intbl Business Partner:\\cell DHL Express\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx4500\\cellx9000\\pard\\intbl Version:\\cell 1.0\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx4500\\cellx9000\\pard\\intbl Effective Date:\\cell ${new Date().toLocaleDateString()}\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\pard\\sa200\\sl276\\slmult1\\cf1\\b\\fs28 1. PURPOSE\\par
\\cf0\\b0\\fs22 This Standard Operating Procedure (SOP) defines the key stroke sequences and system navigation protocols for DHL Express operations. This document ensures consistency, accuracy, and efficiency in system interactions across all operational touchpoints.\\par
\\par
\\cf1\\b\\fs28 2. SCOPE\\par
\\cf0\\b0\\fs22 This SOP applies to all personnel involved in:\\par
\\pard{\\pntext\\f2\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf2\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 Shipment processing and data entry\\par
{\\pntext\\f2\\'B7\\tab}Customer service operations\\par
{\\pntext\\f2\\'B7\\tab}Warehouse management system interactions\\par
{\\pntext\\f2\\'B7\\tab}Order fulfillment and tracking updates\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 3. KEY STROKE SEQUENCES\\par
\\par
\\fs24 3.1 System Login\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf1\\b Step\\cell Action\\cell Key Stroke\\cell Notes\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf0\\b0 1\\cell Open DHL System\\cell Ctrl + Alt + D\\cell Launch application\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 2\\cell Enter User ID\\cell Tab\\cell Navigate to User ID field\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 3\\cell Enter Password\\cell Tab\\cell Move to password field\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 4\\cell Submit\\cell Enter\\cell Login to system\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs24 3.2 Shipment Creation\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf1\\b Step\\cell Action\\cell Key Stroke\\cell Notes\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf0\\b0 1\\cell New Shipment\\cell F2\\cell Open new shipment screen\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 2\\cell Shipper Information\\cell Tab (navigate)\\cell Enter shipper details\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 3\\cell Consignee Information\\cell Tab (navigate)\\cell Enter consignee details\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 4\\cell Package Details\\cell Alt + P\\cell Access package section\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 5\\cell Service Type\\cell Alt + S\\cell Select service level\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 6\\cell Save Shipment\\cell F9\\cell Save and generate AWB\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs24 3.3 Tracking & Updates\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf1\\b Step\\cell Action\\cell Key Stroke\\cell Notes\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl\\cf0\\b0 1\\cell Open Tracking\\cell F3\\cell Access tracking module\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 2\\cell Enter AWB Number\\cell Type + Enter\\cell Search shipment\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 3\\cell Update Status\\cell F5\\cell Refresh status\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2000\\cellx4500\\cellx6500\\cellx9000\\pard\\intbl 4\\cell Add Event\\cell Ctrl + E\\cell Manual event entry\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs24 3.4 Common Navigation\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl\\cf1\\b Function\\cell Key Stroke\\cell Description\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl\\cf0\\b0 Home Screen\\cell Alt + H\\cell Return to main menu\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl Search Function\\cell Ctrl + F\\cell Global search\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl Print\\cell Ctrl + P\\cell Print current document\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl Cancel/Exit\\cell Esc\\cell Cancel current action\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl Save\\cell Ctrl + S\\cell Save current work\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl Help\\cell F1\\cell Context-sensitive help\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 4. DATA ENTRY STANDARDS\\par
\\par
\\fs24 4.1 Field Navigation Rules\\par
\\cf0\\b0\\fs22\\par
\\pard{\\pntext\\f2\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf2\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 Use Tab to move forward between fields\\par
{\\pntext\\f2\\'B7\\tab}Use Shift + Tab to move backward\\par
{\\pntext\\f2\\'B7\\tab}Use Enter only when submitting forms\\par
{\\pntext\\f2\\'B7\\tab}Avoid using mouse clicks when keyboard navigation is available\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs24 4.2 Data Validation\\par
\\cf0\\b0\\fs22\\par
\\pard{\\pntext\\f2\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf2\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 Always verify critical fields before submission\\par
{\\pntext\\f2\\'B7\\tab}Use F5 to refresh and validate data\\par
{\\pntext\\f2\\'B7\\tab}Check for error messages before proceeding\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 5. QUALITY ASSURANCE\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl\\cf1\\b Check Point\\cell Action\\cell Frequency\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl\\cf0\\b0 Data Accuracy\\cell Verify all entered information\\cell Every transaction\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl System Response\\cell Confirm successful submission\\cell Every transaction\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3500\\cellx6000\\cellx9000\\pard\\intbl AWB Generation\\cell Verify AWB number created\\cell Each shipment\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 6. TROUBLESHOOTING\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3000\\cellx6000\\cellx9000\\pard\\intbl\\cf1\\b Issue\\cell Resolution\\cell Key Stroke\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3000\\cellx6000\\cellx9000\\pard\\intbl\\cf0\\b0 System Freeze\\cell Force refresh\\cell Ctrl + F5\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3000\\cellx6000\\cellx9000\\pard\\intbl Unsaved Changes\\cell Quick save\\cell Ctrl + S\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3000\\cellx6000\\cellx9000\\pard\\intbl Need Help\\cell Access help menu\\cell F1\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx3000\\cellx6000\\cellx9000\\pard\\intbl Cancel Operation\\cell Exit current screen\\cell Esc\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 7. COMPLIANCE & AUDIT\\par
\\cf0\\b0\\fs22 All users must:\\par
\\pard{\\pntext\\f2\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf2\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 Follow prescribed key stroke sequences\\par
{\\pntext\\f2\\'B7\\tab}Document any deviations in daily logs\\par
{\\pntext\\f2\\'B7\\tab}Report system anomalies to IT support\\par
{\\pntext\\f2\\'B7\\tab}Participate in quarterly SOP training\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 8. REVISION HISTORY\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl\\cf1\\b Version\\cell Date\\cell Changes\\cell Author\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl\\cf0\\b0 1.0\\cell ${new Date().toLocaleDateString()}\\cell Initial Release\\cell DHL Operations\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\cf1\\b\\fs28 9. APPROVAL\\par
\\cf0\\b0\\fs22\\par
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl\\cf1\\b Role\\cell Name\\cell Signature\\cell Date\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl\\cf0\\b0 Prepared By:\\cell _________________\\cell _________________\\cell _________________\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl Reviewed By:\\cell _________________\\cell _________________\\cell _________________\\cell\\row
\\trowd\\trbrdrt\\brdrs\\brdrw10\\trbrdrl\\brdrs\\brdrw10\\trbrdrb\\brdrs\\brdrw10\\trbrdrr\\brdrs\\brdrw10
\\cellx2250\\cellx4500\\cellx6750\\cellx9000\\pard\\intbl Approved By:\\cell _________________\\cell _________________\\cell _________________\\cell\\row
\\pard\\sa200\\sl276\\slmult1\\par
\\pard\\sa200\\sl276\\slmult1\\qc\\fs20\\par
\\cf1\\b DHL Express - Confidential Document\\par
\\cf0\\b0 This document is the property of DHL Express and may not be reproduced or distributed without authorization.\\par
}`;
          } else if (documentData.type === 'global_statement_of_work') {
            // Create formatted Global Statement of Work for DHL
            sopContent = `{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}{\\f1\\fnil\\fcharset0 Arial;}}
{\\colortbl ;\\red192\\green0\\blue0;\\red0\\green0\\blue0;}
{\\*\\generator Riched20 10.0.19041}\\viewkind4\\uc1 
\\pard\\sa200\\sl276\\slmult1\\qc\\cf1\\b\\f0\\fs28 DHL GLOBAL STATEMENT OF WORK\\par
\\pard\\sa200\\sl276\\slmult1\\cf2\\b0\\fs22\\par
\\b 1. PROJECT OVERVIEW\\b0\\par
Project Name: [Enter Project Name]\\par
Project Code: [Enter Project Code]\\par
Effective Date: [Enter Date]\\par
Version: 1.0\\par
\\par
\\b 2. SCOPE OF WORK\\b0\\par
\\pard{\\pntext\\f1\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf1\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 Objectives: [Enter objectives]\\par
{\\pntext\\f1\\'B7\\tab}Deliverables: [Enter deliverables]\\par
{\\pntext\\f1\\'B7\\tab}Timeline: [Enter timeline]\\par
{\\pntext\\f1\\'B7\\tab}Resources Required: [Enter resources]\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\b 3. RESPONSIBILITIES\\b0\\par
\\b DHL Responsibilities:\\b0\\par
[Enter DHL responsibilities]\\par
\\par
\\b Client Responsibilities:\\b0\\par
[Enter client responsibilities]\\par
\\par
\\b 4. DELIVERABLES AND MILESTONES\\b0\\par
[Enter detailed deliverables and milestones]\\par
\\par
\\b 5. ACCEPTANCE CRITERIA\\b0\\par
[Enter acceptance criteria]\\par
\\par
\\b 6. ASSUMPTIONS AND DEPENDENCIES\\b0\\par
[Enter assumptions and dependencies]\\par
\\par
\\b 7. TERMS AND CONDITIONS\\b0\\par
[Enter terms and conditions]\\par
\\par
\\b 8. APPROVAL\\b0\\par
\\par
Prepared By: ______________________ Date: __________\\par
\\par
Approved By: ______________________ Date: __________\\par
\\f1\\fs20\\par
}`;
          } else if (documentData.type === 'appendix_document') {
            // Create formatted Appendix Document for DHL
            sopContent = `{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}{\\f1\\fnil\\fcharset0 Arial;}}
{\\colortbl ;\\red192\\green0\\blue0;\\red0\\green0\\blue0;}
{\\*\\generator Riched20 10.0.19041}\\viewkind4\\uc1 
\\pard\\sa200\\sl276\\slmult1\\qc\\cf1\\b\\f0\\fs28 DHL APPENDIX DOCUMENT\\par
\\pard\\sa200\\sl276\\slmult1\\cf2\\b0\\fs22\\par
\\b APPENDIX [A/B/C]\\b0\\par
\\par
\\b Document Reference:\\b0 [Enter Reference Number]\\par
\\b Related To:\\b0 [Enter Main Document Name]\\par
\\b Date:\\b0 [Enter Date]\\par
\\b Version:\\b0 1.0\\par
\\par
\\line\\par
\\b SECTION 1: [TITLE]\\b0\\par
\\par
[Enter content for this section]\\par
\\par
\\b Key Points:\\b0\\par
\\pard{\\pntext\\f1\\'B7\\tab}{\\*\\pn\\pnlvlblt\\pnf1\\pnindent0{\\pntxtb\\'B7}}\\fi-360\\li720\\sa200\\sl276\\slmult1 [Point 1]\\par
{\\pntext\\f1\\'B7\\tab}[Point 2]\\par
{\\pntext\\f1\\'B7\\tab}[Point 3]\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\line\\par
\\b SECTION 2: SUPPORTING DOCUMENTATION\\b0\\par
\\par
[Enter supporting documentation details]\\par
\\par
\\b References:\\b0\\par
\\pard{\\pntext\\f1 1.\\tab}{\\*\\pn\\pnlvlbody\\pnf1\\pnindent0\\pnstart1\\pndec{\\pntxta.}}\\fi-360\\li720\\sa200\\sl276\\slmult1 [Reference 1]\\par
{\\pntext\\f1 2.\\tab}[Reference 2]\\par
{\\pntext\\f1 3.\\tab}[Reference 3]\\par
\\pard\\sa200\\sl276\\slmult1\\par
\\line\\par
\\b SECTION 3: ADDITIONAL INFORMATION\\b0\\par
\\par
[Enter additional information]\\par
\\par
\\b Tables/Charts/Diagrams:\\b0\\par
[Insert tables, charts, or diagrams as needed]\\par
\\par
\\line\\par
\\b NOTES:\\b0\\par
[Enter any additional notes or comments]\\par
\\par
\\line\\par
\\b Document Control:\\b0\\par
Created By: [Name] | Date: [Date]\\par
Reviewed By: [Name] | Date: [Date]\\par
Approved By: [Name] | Date: [Date]\\par
\\f1\\fs20\\par
}`;
          }

          fileConfig = {
            content: sopContent,
            type: 'application/rtf',
            extension: 'doc',
            prefix: documentData.type === 'bp_specific_sop_keystroke' ? 'DHL_BP_Specific_SOP_KeyStroke' :
              documentData.type === 'global_sop_keystroke' ? 'Global_SOP_KeyStroke' :
                documentData.type === 'global_statement_of_work' ? 'DHL_Global_Statement_of_Work' :
                  documentData.type === 'appendix_document' ? 'DHL_Appendix_Document' :
                    'document',
            loadingMessage: documentData.type === 'bp_specific_sop_keystroke' ?
              'Creating DHL BP-Specific SOP Key Stroke Document...' :
              documentData.type === 'global_sop_keystroke' ?
                'Creating Global SOP Key Stroke Document...' :
                documentData.type === 'global_statement_of_work' ?
                  'Creating DHL Global Statement of Work...' :
                  documentData.type === 'appendix_document' ?
                    'Creating DHL Appendix Document...' :
                    'Creating Writer Document...',
            successMessage: documentData.type === 'bp_specific_sop_keystroke' ?
              'DHL BP-Specific SOP Key Stroke Document created successfully!' :
              documentData.type === 'global_sop_keystroke' ?
                'Global SOP Key Stroke Document created successfully!' :
                documentData.type === 'global_statement_of_work' ?
                  'DHL Global Statement of Work created successfully!' :
                  documentData.type === 'appendix_document' ?
                    'DHL Appendix Document created successfully!' :
                    'Writer Document created successfully!',
            errorMessage: documentData.type === 'bp_specific_sop_keystroke' ?
              'Error creating DHL BP-Specific SOP Key Stroke Document' :
              documentData.type === 'global_sop_keystroke' ?
                'Error creating Global SOP Key Stroke Document' :
                documentData.type === 'global_statement_of_work' ?
                  'Error creating DHL Global Statement of Work' :
                  documentData.type === 'appendix_document' ?
                    'Error creating DHL Appendix Document' :
                    'Error creating Writer Document'
          }
          break
        case 'calc':
          setCreatingFileType('Calc Spreadsheet')
          fileConfig = {
            content: '',
            type: 'application/vnd.ms-excel',
            extension: 'xls',
            prefix: 'spreadsheet',
            loadingMessage: 'Creating Calc spreadsheet...',
            successMessage: 'Calc spreadsheet created and uploaded successfully!',
            errorMessage: 'Error creating Calc spreadsheet'
          }
          break
        case 'impress':
          setCreatingFileType('Impress Presentation')
          fileConfig = {
            content: '',
            type: 'application/vnd.ms-powerpoint',
            extension: 'ppt',
            prefix: 'presentation',
            loadingMessage: 'Creating Impress presentation...',
            successMessage: 'Impress presentation created and uploaded successfully!',
            errorMessage: 'Error creating Impress presentation'
          }
          break
        default:
          console.error('Unknown document type:', documentData.type)
          return
      }

      setIsCreatingFile(true)
      message.loading(fileConfig.loadingMessage, 0)

      try {
        // Create blob and file
        const blob = new Blob([fileConfig.content], { type: fileConfig.type })
        const filename = `${fileConfig.prefix}_${Date.now()}.${fileConfig.extension}`
        const file = new File([blob], filename, { type: fileConfig.type })

        // Upload the file
        const response = await upload(file, { parent_id: query.parent_id }, general.ORGANIZATION_LIBRARY)

        message.destroy()
        message.success(fileConfig.successMessage)

        // Update document type via API after creating the file
        if (response?.id) {
          const documentTypeMap = {
            'global_sop_keystroke': 1,
            'bp_specific_sop_keystroke': 2,
            'global_statement_of_work': 3,
            'appendix_document': 4,
            'general_document': 5
          };
          const document_type_id = documentTypeMap[documentData.type];
          if (document_type_id) {
            try {
              const apiResponse = await axios.put(
                `${config.aiContentGenerationUrl}sop-management/media/${response.id}/document-type?org_id=${response.organization_id}`,
                { document_type_id, title: documentData.name },
                { headers: { Authorization: `Bearer ${shlToken}`, "Content-Type": "application/json" } }
              )
              console.log("✅ Updated document type successfully:", apiResponse.data)
              setSopData(apiResponse?.data || apiResponse)
            } catch (error) {
              console.error("❌ Document type update failed:", error.response?.data || error.message)
              message.warning('Document created but failed to update document type')
            }
          } else {
            console.error('Unknown document type mapping for:', documentData.type)
          }
        }

        // Show DocEditor after successful upload
        if (response?.path) {
          setDocPath(response.path)
          if (response.title) setDocName(response.title)
        }
      } catch (error) {
        message.destroy()
        message.error(fileConfig.errorMessage)
        console.error(`Error creating/uploading ${documentData.name} file:`, error)
      } finally {
        setIsCreatingFile(false)
        setCreatingFileType('')
        // Reset isCreate to prevent effect from running again
        resetDocumentData();
      }
    }

    createDocument()
  }, [documentData])

  const { t } = useTranslation()

  const handleMenuClick = key => {
    setVisible(false)
    switch (key) {
      case 'file-upload':
        openUploadModal({ parent_id: query.parent_id, privacy_type: '' })
        break
      case 'new-folder':
        setIsCreateFolderModalVisible(true)
        break
      case 'writer':
        // Create and upload empty .docx file
        handleDocxGeneration()
        break
      case 'calc':
        // Create and upload empty Excel file
        handleExcelGeneration()
        break
      case 'impress':
        // Create and upload empty PowerPoint file
        handlePowerPointGeneration()
        break
      case 'screen_recording':
        // Create and upload empty PowerPoint file
        handleScreenRecording()
        break
      default:
        break
    }
  }

  const handleDocxGeneration = () => {
    setSelectedDocType('writer')
    handleDocTypeModalVisible(true)
  }

  const handleExcelGeneration = () => {
    setSelectedDocType('calc')
    handleDocTypeModalVisible(true)
  }

  const handlePowerPointGeneration = () => {
    setSelectedDocType('impress')
    handleDocTypeModalVisible(true)
  }

  const handleScreenRecording_ = async () => {
    const base_Url = config.baseURL.replace(/\/v1\/?$/, '')
    const ai_Url = config.questionAnswerAiUrl
    // const deepLink = `sharelookapp://open?token=${shlToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&course_Id=7488&template_id=7`;
    const deepLink = `sharelookapp://open?token=${shlToken}&refreshToken=${refreshToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&template_id=7`
    try {
      // Method 1: Try direct navigation
      window.location.href = deepLink

      // Method 2: Fallback using a temporary <a> element
      const link = document.createElement('a')
      link.href = deepLink
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Deep link failed:', error)
    }
  }

  const handleScreenRecording = async () => {
    const base_Url = config.baseURL.replace(/\/v1\/?$/, '')
    const ai_Url = config.questionAnswerAiUrl
    console.log('refreshToken log', refreshToken)

    console.log("shlToken:", shlToken);
    console.log("userOrg:", userOrg);
    console.log("refreshToken:", refreshToken);
    console.log("base_Url:", base_Url);
    console.log("ai_Url:", ai_Url);
    console.log("userId:", userId);

    const deepLink = `sharelookapp://open?token=${shlToken}&org_id=${userOrg}&refreshToken=${refreshToken}&base_Url=${base_Url}&ai_Url=${ai_Url}&userId=${userId}&template_id=7`;

    console.log("Generated Deep Link:", deepLink);


    // Show confirmation modal first (provides user gesture)
    showLaunchConfirmation(deepLink)
  }

  // Show confirmation modal using Ant Design
  const showLaunchConfirmation = deepLink => {
    const modal = Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaDesktop style={{ marginRight: '8px', color: '#1890ff' }} />
          {t('v4:launch_sharelook_application')}
        </div>
      ),
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            {t(
              'v4:this_will_open_the_sharelook_application_for_screen_capture'
            )}
          </p>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '0' }}>
            {t('v4:sharelook_dosenot_open')}
          </p>
        </div>
      ),
      okText: (
        <span>
          <FaDesktop style={{ marginRight: '6px' }} />
          {t('v4:open_sharelook')}
        </span>
      ),
      cancelText: t('v4:cancel'),
      okType: 'primary',
      width: 450,
      onOk: () => {
        attemptAppLaunch(deepLink)
      },
      onCancel: () => {
        console.log('User cancelled app launch')
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <div style={{ textAlign: 'center' }}>
          <CancelBtn />
          <OkBtn />
          <Button
            type="default"
            onClick={() => {
              window.open(
                'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                '_blank'
              )
              modal.destroy()
            }}
            style={{ marginLeft: '8px' }}
          >
            <FaDownload style={{ marginRight: '6px' }} />
            {t('v4:download_app')}
          </Button>
        </div>
      )
    })
  }

  // Attempt to launch the app with proper user gesture
  const attemptAppLaunch = deepLink => {
    console.log('Attempting to launch ShareLook with deepLink:', deepLink)

    // Show loading message
    const hide = message.loading(t('v5:opening_sharelook'), 2)

    // Track if app launched successfully
    let appLaunched = false

    const detectAppLaunch = () => {
      const onBlur = () => {
        appLaunched = true
        cleanup()
        hide()
        message.success(t('v5:shareLook_opened_successfully'))
      }

      const onVisibilityChange = () => {
        if (document.hidden) {
          appLaunched = true
          cleanup()
          hide()
          message.success(t('v5:shareLook_opened_successfully'))
        }
      }

      const cleanup = () => {
        window.removeEventListener('blur', onBlur)
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }

      // Listen for app launch indicators
      window.addEventListener('blur', onBlur)
      document.addEventListener('visibilitychange', onVisibilityChange)

      // Check after delay if app didn't launch
      setTimeout(() => {
        cleanup()
        hide()
        if (!appLaunched) {
          showAppNotFound()
        }
      }, 3000)
    }

    try {
      // Method 1: Direct window location (most reliable with user gesture)
      detectAppLaunch()
      window.location.href = deepLink
    } catch (error) {
      console.error('Direct launch failed, trying fallback:', error)

      try {
        // Method 2: Create and click link (fallback)
        const link = document.createElement('a')
        link.href = deepLink
        link.target = '_self'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (fallbackError) {
        console.error('All launch methods failed:', fallbackError)
        hide()
        showAppNotFound()
      }
    }
  }

  // Show message when app launch fails
  const showAppNotFound = () => {
    Modal.error({
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FaExclamationTriangle
            style={{ marginRight: '8px', color: '#ff4d4f' }}
          />
          {t('v4:sharelook_not_found')}
        </div>
      ),
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
            {t('v4:sharelook_screen_capture_application_not_be_opened')}
          </p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                window.open(
                  'https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/windows-screen-capture-app/ELearningSetup.exe',
                  '_blank'
                )
                Modal.destroyAll()
              }}
            >
              <FaDownload style={{ marginRight: '6px' }} />
              {t('v4:download_app')}
            </Button>
          </div>
        </div>
      ),
      okText: t('v4:close'),
      width: 400,
      centered: true
    })
  }

  // Method to check app installation (optional enhancement)
  const checkAppInstallation = () => {
    // This is a simple check - can be enhanced based on your needs
    const isWindows = navigator.platform.indexOf('Win') > -1

    if (!isWindows) {
      message.warning('ShareLook is currently only available for Windows.')
      return false
    }

    return true
  }

  // Enhanced handler with installation check
  const handleScreenRecordingWithCheck = async () => {
    // Check if running on supported platform
    if (!checkAppInstallation()) {
      return
    }

    // Proceed with normal flow
    await handleScreenRecording()
  }

  const handleSetShowEditor = (docFile) => {
    console.log("docFile log", docFile)
    setEditorState({
      editorParams: {
        documentName: docFile?.editorParams?.documentName,  // Access nested property
        documentId: docFile?.editorParams?.documentId,
        userId: docFile?.editorParams?.userId,
        userName: docFile?.editorParams?.userName
      },
      isShowEditor: true
    })
  }

  const menu = (
    <div className="file-menu-dropdown">
      <div className="file-menu-top">
        <div
          className="file-menu-action"
          onClick={() => handleMenuClick('new-folder')}
        >
          <FolderAddOutlined className="menu-icon" />
          <span className="menu-text">{t('v4:new_folder')}</span>
        </div>
        <div
          className="file-menu-action"
          onClick={() => handleMenuClick('file-upload')}
        >
          <FileAddOutlined className="menu-icon" />
          <span className="menu-text">{t('v4:file_upload')}</span>
        </div>
      </div>
      {((isAdmin || isSuperAdmin) && tab && tab === general.ORGANIZATION_LIBRARY) && (
        <div className="file-menu-bottom">
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Writer Document'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('writer')}
          >
            <FaFileWord className="file-menu-app-icon" color="#2b579a" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">{t('v4:writer')}</span>
              <span className="file-menu-app-desc">
                {t('v4:text_documents')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Calc Spreadsheet'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('calc')}
          >
            <FaFileExcel className="file-menu-app-icon" color="#217346" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">Calc</span>
              <span className="file-menu-app-desc">{t('v4:spreadsheets')}</span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Impress Presentation'
              ? 'creating'
              : ''
              }`}
            onClick={() => !isCreatingFile && handleMenuClick('impress')}
          >
            <FaFilePowerpoint className="file-menu-app-icon" color="#d24726" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">{t('v4:impress')}</span>
              <span className="file-menu-app-desc">
                {t('v4:presentations')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
          <div
            className={`file-menu-app ${isCreatingFile && creatingFileType === 'Screen Recording'
              ? 'opening desktop app'
              : ''
              }`}
            onClick={() =>
              !isCreatingFile && handleMenuClick('screen_recording')
            }
          >
            <FaDesktop className="file-menu-app-icon" color="#d24726" />
            <div className="file-menu-app-labels">
              <span className="file-menu-app-title">
                {t('v4:capture_desktop_screen')}
              </span>
              <span className="file-menu-app-desc">
                {t('v4:capture_desktop_screens')}
              </span>
            </div>
            <RightOutlined className="file-menu-app-arrow" />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="document-header">
      <Modal
        visible={editorState?.isShowEditor}
        onCancel={() => setEditorState(prev => ({ ...prev, isShowEditor: false }))}
        footer={null}
        width="90%"
        bodyStyle={{ height: '80vh', padding: 0 }}
        destroyOnClose={true}
        maskClosable={false}
      >
        <DocEditor
          orgUserList={orgUserList}
          isDocOpened={editorState?.isShowEditor}
          documentName={editorState?.editorParams?.documentName || docEditorFile?.title || docName}
          documentId={editorState?.editorParams?.documentId || docEditorFile?.path || docPath}
          userId={editorState?.editorParams?.userId || docEditorFile?.creator?.id || userId}
          userName={editorState?.editorParams?.userName || docEditorFile?.creator?.username || userName}
          closeModal={() => setEditorState(prev => ({ ...prev, isShowEditor: false }))}
          shlToken={shlToken}
          {...getUserPermissions(user, orgUserList, docEditorFile, true)}
          //isEdit
          //isView,
          orgId={userOrg}
        />
      </Modal>
      {((docEditorFile && Object.keys(docEditorFile).length > 0) || (isSOPModalVisible && sopData && Object.keys(sopData).length > 0)) ? (
        <Modal
          visible={
            (docEditorFile && Object.keys(docEditorFile).length > 0) ||
            (isSOPModalVisible && sopData && Object.keys(sopData).length > 0)
          }
          onCancel={() => {
            setIsSOPModalVisible(false);
            // Add logic to clear docEditorFile if needed
            // setDocEditorFile(null);
          }}
          footer={null}
          width="100%"
          style={{ maxWidth: '1500px', top: 20 }}
          bodyStyle={{ minHeight: '60vh', padding: '24px' }}
          destroyOnClose
        >
          <SOPModal
            docEditorFile={docEditorFile}
            data={sopData}
            userId={userId}
            userName={userName}
            orgId={userOrg}
            shlToken={shlToken}
            setEditor={handleSetShowEditor}
          />
        </Modal>
      ) : (
        <>
          <div className="header-left">
            {defaultQuery.isActiveOrg && (
              <>
                <div className="header-left-title">{t('v4:your_documents')}</div>
                <div className="header-left-description">
                  {t('v4:manage_your_videos_images_audio_files')}
                </div>
              </>
            )}

            {defaultQuery.isOrg && (
              <>
                <div className="header-left-title">
                  {t('v4:your_organization_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:keep_your_organization_creative_assets_organized')}
                </div>
              </>
            )}

            {defaultQuery.isPublic && (
              <>
                <div className="header-left-title">
                  {t('v4:your_public_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:curated_collection_of_media_assets_available')}
                </div>
              </>
            )}

            {defaultQuery.isDownload && (
              <>
                <div className="header-left-title">
                  {t('v4:your_download_center_documents')}
                </div>
                <div className="header-left-description">
                  {t('v4:repository_of_approved_media_assets_for_download')}
                </div>
              </>
            )}
          </div>

          <div className="header-right">
            <Search
              placeholder={t('v4:search_files_and_folders')}
              defaultValue={query.search}
              onChange={handleChangeSearch}
              className="header-right-search"
              allowClear
            />

            {!defaultQuery.isDownload &&
              ((tab === 'My Media' && (!isAdmin || !isSuperAdmin)) ||
                (tab !== 'Public Media' && (isAdmin || isSuperAdmin))) && (
                <div className="header-actions">
                  <Dropdown
                    overlay={menu}
                    trigger={['click']}
                    visible={visible}
                    onVisibleChange={setVisible}
                    placement="bottomRight"
                    overlayClassName="custom-dropdown"
                  >
                    <Button type="primary" icon="plus" loading={isCreatingFile}>
                      {isCreatingFile
                        ? `Creating ${creatingFileType}...`
                        : t('v4:new')}
                    </Button>
                  </Dropdown>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  )
}

export default DocumentHeader